import { useId, type ComponentPropsWithRef, type ReactNode } from "react";

import { cn } from "../../lib/cn";
import { Badge } from "../badge";

export interface CloudRegion {
  id: string;
  code: string;
  name: string;
  location: string;
  flag: string;
  latencyMs?: number;
  compliance?: string[];
  status?: "operational" | "degraded" | "maintenance";
}

export const CLOUD_REGIONS_DEFAULT: CloudRegion[] = [
  {
    id: "eu-west-3",
    code: "eu-west-3",
    name: "Europe (Paris)",
    location: "Paris, France",
    flag: "🇫🇷",
    latencyMs: 12,
    compliance: ["GDPR", "HIPAA", "HDS"],
    status: "operational",
  },
  {
    id: "eu-central-1",
    code: "eu-central-1",
    name: "Europe (Frankfurt)",
    location: "Frankfurt, Allemagne",
    flag: "🇩🇪",
    latencyMs: 24,
    compliance: ["GDPR", "SOC2"],
    status: "operational",
  },
  {
    id: "us-east-1",
    code: "us-east-1",
    name: "US East (N. Virginia)",
    location: "Ashburn, VA, USA",
    flag: "🇺🇸",
    latencyMs: 85,
    compliance: ["SOC1", "SOC2", "HIPAA"],
    status: "operational",
  },
  {
    id: "us-west-2",
    code: "us-west-2",
    name: "US West (Oregon)",
    location: "Oregon, USA",
    flag: "🇺🇸",
    latencyMs: 140,
    compliance: ["SOC2", "HIPAA"],
    status: "operational",
  },
  {
    id: "ap-northeast-1",
    code: "ap-northeast-1",
    name: "Asia Pacific (Tokyo)",
    location: "Tokyo, Japon",
    flag: "🇯🇵",
    latencyMs: 210,
    compliance: ["SOC2", "ISMAP"],
    status: "operational",
  },
];

export interface RegionSelectorProps extends Omit<ComponentPropsWithRef<"div">, "onChange" | "onSelect"> {
  regions?: CloudRegion[];
  selectedRegionId?: string;
  selectedRegionIds?: string[];
  multiple?: boolean;
  onSelect?: (region: CloudRegion) => void;
  onMultiSelectChange?: (regionIds: string[]) => void;
  label?: ReactNode;
}

/**
 * Sélecteur de région cloud avec latence temps réel et tags de conformité.
 *
 * ── Composants propriétaires ──────────────────────────────────────────────
 * Utilisation directe du composant Badge pour les latences et labels de conformité.
 */
export function RegionSelector({
  regions = CLOUD_REGIONS_DEFAULT,
  selectedRegionId,
  selectedRegionIds = [],
  multiple = false,
  onSelect,
  onMultiSelectChange,
  label = "Région d'hébergement",
  className,
  ...props
}: RegionSelectorProps) {
  const selectId = useId();

  const isSelected = (id: string) => {
    if (multiple) {
      return selectedRegionIds.includes(id);
    }
    return selectedRegionId === id;
  };

  const handleToggle = (region: CloudRegion) => {
    if (multiple) {
      const exists = selectedRegionIds.includes(region.id);
      const next = exists
        ? selectedRegionIds.filter((id) => id !== region.id)
        : [...selectedRegionIds, region.id];
      onMultiSelectChange?.(next);
    } else {
      onSelect?.(region);
    }
  };

  return (
    <div className={cn("flex flex-col gap-2.5", className)} {...props}>
      {label && (
        <label id={selectId} className="text-xs font-semibold uppercase tracking-wider text-fg-muted">
          {label}
        </label>
      )}

      <div
        role="group"
        aria-labelledby={selectId}
        className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-3"
      >
        {regions.map((region) => {
          const active = isSelected(region.id);
          const latency = region.latencyMs;

          const latencyTone =
            typeof latency === "number"
              ? latency < 50
                ? "success"
                : latency < 120
                ? "warning"
                : "neutral"
              : "neutral";

          return (
            <button
              key={region.id}
              type="button"
              onClick={() => handleToggle(region)}
              aria-pressed={active}
              className={cn(
                "group relative flex flex-col justify-between gap-3 rounded-xl border p-3.5 text-left transition-all duration-fast ease-standard focus-visible:outline-2 focus-visible:outline-ring cursor-pointer",
                active
                  ? "border-accent bg-accent-subtle/30 shadow-xs ring-1 ring-accent"
                  : "border-border bg-surface-raised hover:border-border-strong hover:bg-surface-raised/80",
              )}
            >
              {/* En-tête avec Drapeau & Code */}
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2.5">
                  <span className="text-xl leading-none select-none">{region.flag}</span>
                  <div className="flex flex-col">
                    <span className="text-sm font-semibold text-fg">{region.name}</span>
                    <span className="font-mono text-[11px] text-fg-muted">{region.code}</span>
                  </div>
                </div>

                {/* Badge de Latence via Badge design system */}
                {typeof latency === "number" && (
                  <Badge tone={latencyTone} size="sm" dot>
                    {region.latencyMs} ms
                  </Badge>
                )}
              </div>

              {/* Pied de carte avec Badges de conformité */}
              {region.compliance && region.compliance.length > 0 && (
                <div className="flex flex-wrap items-center gap-1.5 pt-2 border-t border-border/40">
                  {region.compliance.map((tag) => (
                    <Badge key={tag} tone="neutral" size="sm" className="text-[10px] uppercase font-mono">
                      {tag}
                    </Badge>
                  ))}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
